import { useCallback, useEffect, useState } from 'react';
import { platformApi } from '../../api/platform';
import { useAuth } from '../../auth/AuthProvider';
import {
  EmptyState,
  ErrorBanner,
  LoadingState,
  Modal,
  PageHeader,
} from '../../components/Layout';

const emptyForm = { email: '', first_name: '', last_name: '', password: '' };

export default function StaffPage() {
  const { getToken } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [createdPassword, setCreatedPassword] = useState(null);

  const loadStaff = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await platformApi.listStaff(token);
      setStaff(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const handleCreate = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setCreatedPassword(null);
    try {
      const token = await getToken();
      const payload = {
        email: form.email,
        first_name: form.first_name,
        last_name: form.last_name,
      };
      if (form.password) {
        payload.password = form.password;
      }
      const created = await platformApi.createStaff(token, payload);
      setModalOpen(false);
      setForm(emptyForm);
      if (created.temporary_password) {
        setCreatedPassword({
          email: created.email,
          password: created.temporary_password,
        });
      }
      await loadStaff();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (userId) => {
    if (!window.confirm('Deactivate this staff user? They will no longer be able to sign in.')) {
      return;
    }
    setError(null);
    try {
      const token = await getToken();
      await platformApi.deactivateStaff(token, userId);
      await loadStaff();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return <LoadingState />;
  }

  return (
    <>
      <PageHeader
        title="Platform staff"
        description="Internal staff who can manage shops and assign shop admins. Only superadmins can manage staff accounts."
        actions={
          <button type="button" className="btn btn-primary" onClick={() => setModalOpen(true)}>
            Add staff
          </button>
        }
      />

      <ErrorBanner message={error} />

      {createdPassword && (
        <div className="card" style={{ marginBottom: '1rem', borderColor: 'var(--accent)' }}>
          <div className="card-body">
            <strong>Staff account created.</strong> Share these login credentials with the new user.
            They sign in at the platform dashboard using the <code>salesbotics-platform</code> client.
            <p style={{ margin: '0.75rem 0 0' }}>
              Email: <strong>{createdPassword.email}</strong>
              <br />
              Temporary password: <strong>{createdPassword.password}</strong>
            </p>
          </div>
        </div>
      )}

      {staff.length === 0 ? (
        <EmptyState message="No platform staff yet. Add staff users so they can help manage shops." />
      ) : (
        <div className="card">
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {staff.map((member) => (
                  <tr key={member.id}>
                    <td>
                      {member.first_name} {member.last_name}
                    </td>
                    <td>{member.email}</td>
                    <td>
                      <span className="badge badge-info">{member.platform_role}</span>
                    </td>
                    <td>
                      <span className={`badge ${member.is_active ? 'badge-success' : 'badge-muted'}`}>
                        {member.is_active ? 'active' : 'inactive'}
                      </span>
                    </td>
                    <td>
                      {member.platform_role !== 'superadmin' && member.is_active && (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => handleDeactivate(member.id)}
                        >
                          Deactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Modal
        title="Add platform staff"
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        footer={
          <>
            <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </button>
            <button type="submit" form="create-staff-form" className="btn btn-primary" disabled={saving}>
              {saving ? 'Creating…' : 'Create staff'}
            </button>
          </>
        }
      >
        <form id="create-staff-form" className="form-grid" onSubmit={handleCreate}>
          <div className="form-field">
            <label htmlFor="staff-email">Email</label>
            <input
              id="staff-email"
              type="email"
              value={form.email}
              onChange={(event) => setForm((prev) => ({ ...prev, email: event.target.value }))}
              required
            />
          </div>
          <div className="form-grid two-col">
            <div className="form-field">
              <label htmlFor="staff-first">First name</label>
              <input
                id="staff-first"
                value={form.first_name}
                onChange={(event) => setForm((prev) => ({ ...prev, first_name: event.target.value }))}
                required
              />
            </div>
            <div className="form-field">
              <label htmlFor="staff-last">Last name</label>
              <input
                id="staff-last"
                value={form.last_name}
                onChange={(event) => setForm((prev) => ({ ...prev, last_name: event.target.value }))}
                required
              />
            </div>
          </div>
          <div className="form-field">
            <label htmlFor="staff-password">Password (optional)</label>
            <input
              id="staff-password"
              type="password"
              value={form.password}
              onChange={(event) => setForm((prev) => ({ ...prev, password: event.target.value }))}
              placeholder="Leave blank to auto-generate a temporary password"
              minLength={8}
            />
          </div>
        </form>
      </Modal>
    </>
  );
}
